import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
var _excluded = ["icon"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle, useMemo, useContext } from 'react';
import clsx from 'clsx';
import Icon from '../icon';
import Cell from '../cell';
import Dialog from '../dialog';
import { isDef, addUnit, formatNumber, isObject, preventDefault, resetScroll } from '../utils';
import ConfigProviderContext from '../config-provider/ConfigProviderContext';
import { COMPONENT_TYPE_KEY } from '../utils/constant';
var ICON_SIZE = '16px';
var Field = /*#__PURE__*/forwardRef(function (props, ref) {
  var _bem;

  var _useContext = useContext(ConfigProviderContext),
      prefixCls = _useContext.prefixCls,
      createNamespace = _useContext.createNamespace;

  var _createNamespace = createNamespace('field', prefixCls),
      _createNamespace2 = _slicedToArray(_createNamespace, 1),
      bem = _createNamespace2[0];

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      inputFocus = _useState2[0],
      setInputFocus = _useState2[1];

  var fieldRef = useRef(null);
  var inputRef = useRef(null);
  useEffect(function () {
    if (props.getFieldRef) props.getFieldRef(fieldRef);
    if (props.getInputRef) props.getInputRef(inputRef);
  }, [props.getFieldRef, props.getInputRef]);

  var focus = function focus() {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  var blur = function blur() {
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  useImperativeHandle(ref, function () {
    return {
      focus: focus,
      blur: blur
    };
  });

  var getProp = function getProp(key) {
    if (isDef(props[key])) {
      return props[key];
    }

    return null;
  };

  var getModelValue = function getModelValue() {
    var _props$value;

    return String((_props$value = props.value) !== null && _props$value !== void 0 ? _props$value : '');
  };

  var showClear = useMemo(function () {
    var readonly = getProp('readonly');

    if (props.clearable && !readonly) {
      var hasValue = getModelValue() !== '';
      var trigger = props.clearTrigger === 'always' || props.clearTrigger === 'focus' && inputFocus;
      return hasValue && trigger;
    }

    return false;
  }, [props.value, props.clearTrigger, inputFocus]);

  var labelStyle = function labelStyle() {
    var labelW = getProp('labelWidth');

    if (labelW) {
      return {
        width: addUnit(labelW)
      };
    }

    return {};
  };

  var adjustSize = function adjustSize() {
    var input = inputRef.current;

    if (!(props.type === 'textarea' && props.autosize) || !input) {
      return;
    }

    input.style.height = 'auto';
    var height = input.scrollHeight;

    if (isObject(props.autosize)) {
      var _props$autosize = props.autosize,
          maxHeight = _props$autosize.maxHeight,
          minHeight = _props$autosize.minHeight;

      if (maxHeight) {
        height = Math.min(height, maxHeight);
      }

      if (minHeight) {
        height = Math.max(height, minHeight);
      }
    }

    if (height) {
      input.style.height = "".concat(height, "px");
    }
  };

  useEffect(function () {
    adjustSize();
  }, [props.value]);

  var formatValue = function formatValue(inputValue) {
    var trigger = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'onChange';
    var formatTrigger = props.formatTrigger,
        formatter = props.formatter;

    if (formatter && trigger === formatTrigger) {
      return formatter(inputValue);
    }

    return inputValue;
  };

  var renderInput = function renderInput() {
    var type = props.type,
        error = props.error,
        name = props.name,
        rows = props.rows,
        value = props.value,
        placeholder = props.placeholder,
        disabled = props.disabled,
        readonly = props.readonly;
    var controlClass = bem('control', [getProp('inputAlign'), {
      error: error,
      custom: !!props.children,
      'min-height': props.type === 'textarea' && !props.autosize
    }]);

    if (props.children) {
      return /*#__PURE__*/React.createElement("div", {
        className: clsx(controlClass)
      }, props.children);
    }

    var handleChange = function handleChange(e) {
      var _e$currentTarget;

      var maxlength = props.maxlength,
          onChange = props.onChange;
      var inputValue = e === null || e === void 0 ? void 0 : (_e$currentTarget = e.currentTarget) === null || _e$currentTarget === void 0 ? void 0 : _e$currentTarget.value;
      var finalValue = inputValue;

      if (isDef(maxlength) && finalValue.length > +maxlength) {
        finalValue = finalValue.slice(0, maxlength);
      }

      if (type === 'number' || type === 'digit') {
        var isNumber = type === 'number';
        finalValue = formatNumber(finalValue, isNumber, isNumber);
      }

      finalValue = formatValue(finalValue, 'onChange'); // if (inputRef.value && inputValue !== inputRef.value.value) {
      //   inputRef.value.value = inputValue;
      // }

      if (onChange && typeof onChange === 'function') {
        onChange(finalValue);
      }
    };

    var handleFocus = function handleFocus(e) {
      var onFocus = props.onFocus;
      setInputFocus(true);

      if (onFocus && typeof onFocus === 'function') {
        onFocus(e);
      } // readonly not work in legacy mobile safari


      if (readonly) {
        blur();
      }
    };

    var handleBulr = function handleBulr(e) {
      var onBlur = props.onBlur;
      setInputFocus(false);

      if (onBlur && typeof onBlur === 'function') {
        onBlur(e);
      }

      resetScroll();
    };

    var handleKeypress = function handleKeypress(e) {
      var onKeypress = props.onKeypress;

      if (e.key === 'Enter' || +e.charCode === 13) {
        if (props.type !== 'textarea') {
          preventDefault(e);
        } // trigger blur after click keyboard search button


        if (props.type === 'search') {
          blur();
        }
      }

      if (onKeypress && typeof onKeypress === 'function') {
        onKeypress(e);
      }
    };

    if (type === 'textarea') {
      return /*#__PURE__*/React.createElement("textarea", {
        ref: inputRef,
        name: name,
        rows: rows,
        className: clsx(controlClass),
        value: value,
        disabled: disabled,
        readOnly: readonly,
        placeholder: placeholder || '',
        onBlur: handleBulr,
        onFocus: handleFocus,
        onChange: handleChange,
        onKeyPress: handleKeypress
      });
    }

    var inputType = type;
    var inputMode; // type="number" is weired in iOS, and can't prevent dot in Android
    // so use inputmode to set keyboard in mordern browers

    if (type === 'number') {
      inputType = 'text';
      inputMode = 'decimal';
    }

    if (type === 'digit') {
      inputType = 'tel';
      inputMode = 'numeric';
    }

    return /*#__PURE__*/React.createElement("input", {
      value: value,
      type: inputType,
      inputMode: inputMode,
      ref: inputRef,
      name: name,
      className: clsx(controlClass),
      disabled: disabled,
      readOnly: readonly,
      placeholder: placeholder || '',
      onBlur: handleBulr,
      onFocus: handleFocus,
      onChange: handleChange,
      onKeyPress: handleKeypress
    });
  };

  var renderLeftIcon = function renderLeftIcon() {
    var leftIcon = props.leftIcon,
        onClickLeftIcon = props.onClickLeftIcon;
    if (!leftIcon) return null;
    return /*#__PURE__*/React.createElement("div", {
      className: clsx(bem('left-icon')),
      onClick: onClickLeftIcon
    }, typeof leftIcon !== 'string' ? leftIcon : /*#__PURE__*/React.createElement(Icon, {
      name: leftIcon,
      classPrefix: props.iconPrefix
    }));
  };

  var renderRightIcon = function renderRightIcon() {
    var rightIcon = props.rightIcon,
        iconPrefix = props.iconPrefix,
        onClickRightIcon = props.onClickRightIcon;
    if (!rightIcon) return null;
    return /*#__PURE__*/React.createElement("div", {
      className: clsx(bem('right-icon')),
      onClick: onClickRightIcon
    }, typeof rightIcon === 'string' ? /*#__PURE__*/React.createElement(Icon, {
      name: rightIcon,
      classPrefix: iconPrefix
    }) : rightIcon);
  };

  var renderWordLimit = function renderWordLimit() {
    var value = props.value,
        showWordLimit = props.showWordLimit,
        maxlength = props.maxlength;

    if (showWordLimit && maxlength) {
      var count = (value ? "".concat(value) : '').length;
      return /*#__PURE__*/React.createElement("div", {
        className: clsx(bem('word-limit'))
      }, /*#__PURE__*/React.createElement("span", {
        className: clsx(bem('word-num'))
      }, count), "/", maxlength);
    }

    return null;
  };

  var renderMessage = function renderMessage() {
    var message = props.errorMessage;

    if (message) {
      var errorMessageAlign = getProp('errorMessageAlign');
      return /*#__PURE__*/React.createElement("div", {
        className: clsx(bem('error-message', errorMessageAlign))
      }, message);
    }

    return null;
  };

  var renderIntro = function renderIntro() {
    if (props.intro) {
      return /*#__PURE__*/React.createElement("div", {
        className: clsx(bem('intro', getProp('inputAlign')))
      }, props.intro);
    }

    return null;
  };

  var renderTooltip = function renderTooltip() {
    var tooltip = props.tooltip;

    if (tooltip) {
      var icon = /*#__PURE__*/React.createElement(Icon, {
        name: "question-o"
      });
      var dialogProps = {
        message: tooltip
      };

      if (!( /*#__PURE__*/React.isValidElement(tooltip) || typeof tooltip === 'string')) {
        var _ref = tooltip,
            customIcon = _ref.icon,
            customDialogProps = _objectWithoutProperties(_ref, _excluded);

        icon = customIcon || icon;
        dialogProps = customDialogProps;
      }

      return /*#__PURE__*/React.createElement("div", {
        className: clsx(bem('tooltip')),
        onClick: function onClick() {
          return Dialog.show(dialogProps);
        }
      }, icon);
    }

    return null;
  };

  var renderLabel = function renderLabel() {
    var label = props.label,
        colon = props.colon;

    if (label) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, label, colon && ':', renderTooltip());
    }

    return null;
  };

  var handleClear = function handleClear(e) {
    var onClear = props.onClear,
        onChange = props.onChange;
    inputRef.current.value = '';

    if (onChange && typeof onChange === 'function') {
      onChange('');
    }

    if (onClear && typeof onClear === 'function') {
      onClear(e);
    }
  };

  var type = props.type,
      size = props.size,
      center = props.center,
      border = props.border,
      isLink = props.isLink,
      required = props.required,
      clickable = props.clickable,
      labelAlign = props.labelAlign,
      className = props.className,
      labelClass = props.labelClass,
      valueClass = props.valueClass,
      arrowDirection = props.arrowDirection,
      autosize = props.autosize,
      disabled = props.disabled,
      button = props.button,
      titleStyle = props.titleStyle,
      error = props.error;
  return /*#__PURE__*/React.createElement(Cell, {
    title: renderLabel(),
    size: size,
    icon: renderLeftIcon(),
    center: center,
    border: border,
    isLink: isLink,
    required: required,
    clickable: clickable,
    extra: props.extra,
    titleStyle: _objectSpread(_objectSpread({}, labelStyle()), titleStyle),
    valueClass: clsx(bem('value'), valueClass),
    titleClass: clsx(bem('label', labelAlign), labelClass),
    arrowDirection: arrowDirection,
    onClick: props.onClick,
    style: props.style,
    className: clsx(bem((_bem = {
      error: error,
      disabled: disabled
    }, _defineProperty(_bem, "label-".concat(labelAlign), labelAlign), _defineProperty(_bem, 'min-height', type === 'textarea' && !autosize), _bem)), className)
  }, /*#__PURE__*/React.createElement("div", {
    className: clsx(bem('body'))
  }, props.prefix && /*#__PURE__*/React.createElement("div", {
    className: clsx(bem('prefix'))
  }, props.prefix), /*#__PURE__*/React.createElement("div", {
    className: clsx(bem('control-wrapper')),
    onClick: props.onClickInput
  }, renderInput()), showClear && (typeof props.clearIcon === 'string' ? /*#__PURE__*/React.createElement(Icon, {
    className: clsx(bem('clear')),
    onTouchStart: handleClear,
    name: props.clearIcon,
    size: ICON_SIZE
  }) : props.clearIcon), renderRightIcon(), button && /*#__PURE__*/React.createElement("div", {
    className: clsx(bem('button'))
  }, button)), renderWordLimit(), renderMessage(), renderIntro());
});
Field.defaultProps = {
  clearIcon: 'clear',
  clearTrigger: 'focus',
  formatTrigger: 'onChange'
};
export var FIELD_KEY = Symbol('field');
var FieldNamespace = Object.assign(Field, _defineProperty({}, COMPONENT_TYPE_KEY, FIELD_KEY));
export default FieldNamespace;